import React, { useEffect, useState } from "react"
import loadable from "@loadable/component"

import { useSiteContext } from "../common/SiteContext"
import { graphql, Link, navigate, useStaticQuery } from "gatsby"
import {
  BackgroundImage,
} from "@organic-return/foundation-react/src/components/Media"
import { EWsrvTransform, IMediaItem } from "@organic-return/foundation-react/src/lib"

const LocationSearch = loadable(() => import("@organic-return/foundation-react/src/components/Forms/LocationSearch"));

const LocationAutocompleteSearch = ({ module }) => {
  const data = useStaticQuery(graphql`
  query ListingSearchAutocompleteQuery {
    config: agilitySiteConfiguration(properties:{referenceName:{ne:"siteconfiguration_deleted"}}) {
      customFields {
        listingSearchPath
      }
    }
    all_city: allListing(filter: {status: {ne: "Closed"}})  {
      distinct(field: address___city)
    }
    all_county: allListing(filter: {status: {ne: "Closed"}}) {
      distinct(field: address___county)
    }
    all_postal: allListing(filter: {status: {ne: "Closed"}}) {
      distinct(field: address___postal)
    }
    all_majorArea: allListing(filter: {status: {ne: "Closed"}}) {
      distinct(field: majorArea)
    }
    all_minorArea: allListing(filter: {status: {ne: "Closed"}}) {
      distinct(field: minorArea)
    }
    all_subdivision: allListing(filter: {status: {ne: "Closed"}}) {
      distinct(field: subdivision)
    }
    allListing(filter: {status: {ne: "Closed"}})  {
      nodes {
        address {
          text
        }
        mlsID
      }
    }
    allAgilityLinkItem {
      nodes {
        properties {
          referenceName
        }
        customFields {
          icon {
            url
          }
          url {
            text
            href
            target
          }
        }
        properties {
          itemOrder
        }
      }
    }
    allAgilityQuickSearchLink {
      nodes {
        customFields {
          backgroundImage {
            url
          }
          backgroundVideo {
            url
          }
          icon {
            url
          }
          url {
            text
            href
            target
          }
        }
        properties {
          itemOrder
          referenceName
        }
      }
    }
  }
`)
  const siteContext = useSiteContext();
  const fields = module.customFields;
  let items = data.all_city.distinct;
  items.push(...data.all_county.distinct);
  items.push(...data.all_postal.distinct);
  items.push(...data.all_majorArea.distinct);
  items.push(...data.all_minorArea.distinct);
  items.push(...data.all_subdivision.distinct);
  data.allListing.nodes && data.allListing.nodes.forEach((node) => {
    items.push(node.mlsID + " - " + node.address.text);
  })

  items = [...new Set(items.sort())]

  const component = (
    <div className="absolute w-full h-full inset-0" style={{ backgroundImage: "linear-gradient(to right, rgba(5, 5, 5, 1), rgba(0, 0, 0, 0))" }}>
      <div className="absolute max-w-screen-2xl mx-auto top-5 inset-x-5 md:top-10 md:inset-x-10">
        <LocationSearch
          className={"text-composite-text w-full lg:w-3/4 xl:w-1/2"}
          title={fields.title}
          titleClassName={"text-left uppercase text-xl md:text-3xl text-composite-text pt-32"}
          buttonClassName={"text-button-text uppercase font-bold"}
          buttonInline={false}
          itemsToSearch={items}
          onSubmit={async filters => {
            const params = new URLSearchParams({
              filters: JSON.stringify(filters),
            })
            await navigate(
              data.config.customFields.listingSearchPath + "#" + params.toString() + "&showMap=true"
            )
          }

          }
          {...siteContext.locationSearchProps}
        />
      </div>
    </div>
  )

  let lgImageBg: IMediaItem = fields.backgroundImage?.url && {
    url: fields.backgroundImage.url,
  };

  const sortByItemOrder = (a, b) => a.properties.itemOrder - b.properties.itemOrder

  let allAgilityLinkItems = data.allAgilityLinkItem?.nodes?.filter((item) => {
    return fields.quickLinks?.referencename == item.properties.referenceName
  }) ?? [];
  let allAgilityQuickSearchLinks = data.allAgilityQuickSearchLink?.nodes?.filter((item) => {
    return fields.quickLinks?.referencename == item.properties.referenceName
  }) ?? [];
  let quickLinks = allAgilityQuickSearchLinks.length > 0 ? allAgilityQuickSearchLinks : allAgilityLinkItems;

  quickLinks = quickLinks.sort(sortByItemOrder)

  const [activeLink, setActiveLink] = useState(0)

  return (
    <div className="relative location-autocomplete-search w-full">
      {fields.backgroundVideo?.url && (
        <div className="relative h-screen w-full">
          <video
            muted={true}
            autoPlay={true}
            loop={true}
            className="absolute inset-0 w-full h-full object-center object-cover"
          >
            <source src={fields.backgroundVideo.url}></source>
          </video>
        </div>
      )}
      {!fields.backgroundVideo?.url && lgImageBg && (
        <BackgroundImage
          src={lgImageBg.url}
          overlayGradient={"linear-gradient(to right, rgba(5, 5, 5, 1), rgba(0, 0, 0, 0)),"}
          className="object-cover h-screen relative"
          sizesAndAspect={[
            {
              width: 420,
              aspect: EWsrvTransform.cover2x6,
            },
            {
              media: "min-width:420px",
              width: 640,
              aspect: EWsrvTransform.cover2x6,
            },
            {
              media: "min-width:640px",
              width: 768,
              aspect: EWsrvTransform.cover3x2,
            },
            {
              media: "min-width:768px",
              width: 1024,
              aspect: EWsrvTransform.cover3x2,
            },
            {
              media: "min-width:1024px",
              width: 1280,
              aspect: EWsrvTransform.cover16x9,
            },
            {
              media: "min-width:1280px",
              width: 1440,
              aspect: EWsrvTransform.cover16x9,
            },
            {
              media: "min-width:1440px",
              width: 1920,
              aspect: EWsrvTransform.cover16x9,
            },
            {
              media: "min-width:1920px",
              width: 2560,
              aspect: EWsrvTransform.cover16x9,
            },
          ]}
        >
        </BackgroundImage>
      )}
      {quickLinks && quickLinks.length > 0 && (
        <>
          {quickLinks.map((link, linkIndex) => (
            <>
              {link.customFields?.backgroundVideo?.url && (
                <video
                  src={link.customFields.backgroundVideo.url}
                  className={`object-cover w-full h-full absolute inset-0 ${activeLink == linkIndex ? "block" : "hidden"}`}
                  muted
                  autoPlay
                  controls={false}
                  loop
                >
                </video>
              )}
              {!link.customFields?.backgroundVideo?.url && link.customFields?.backgroundImage?.url && (
                <img
                  src={link.customFields.backgroundImage.url}
                  className={`object-cover w-full h-full absolute inset-0 ${activeLink == linkIndex ? "block" : "hidden"}`}
                />
              )}
            </>
          ))}
        </>
      )}
      {component}
      {quickLinks && quickLinks.length > 0 && (
        <div className="absolute inset-x-0 bottom-0 flex flex-row justify-center flex-wrap md:flex-nowrap gap-x-5 md:gap-x-20 lg:gap-x-32 xl:gap-x-40 pb-12 border-t">
          {quickLinks.map((link, linkIndex) => (
            <Link to={link.customFields.url.href}
              onMouseOver={() => { setActiveLink(linkIndex) }}
              className={`transition-[border-color] ease-linear relative md:-top-1 w-auto pt-4 flex flex-col items-center gap-y-6 text-composite-text hover:text-composite-text md:border-t-4 md:hover:border-composite-text ${activeLink == linkIndex ? "md:border-composite-text" : "md:border-transparent"}`}>
              {link.customFields.url.text}
              <img src={link.customFields.icon.url} />
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default LocationAutocompleteSearch
